import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useEffect } from 'react';

import { ENDPOINT } from '~/constants/endpoints';
import {
  PAGINATION_PAGE_SIZE_DEFAULT,
  type PaginationSortDirection,
} from '~/constants/pagination';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { toSnakeCase, withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import {
  createInfiniteQuery,
  type InfiniteQueryOptionsType,
} from '~/utils/createInfiniteQuery';

import { queryKeysCostCenter } from './queryKeys';

export type CostCentersQueryParams = {
  limit: number;
  offset: number;
  searchString: string;
  filterAccRefType: 'cost_center' | 'profit_center';
  filterActive: boolean;
  filterCompany: string;
  filterNotParentOu?: UUID;
  filterParentOu?: UUID;
  orderBy:
    | 'company_id'
    | 'company_name'
    | 'created_on'
    | 'end'
    | 'id'
    | 'is_active'
    | 'modified_on'
    | 'name'
    | 'start'
    | 'type';
  returnTotalCount: boolean;
  sort: PaginationSortDirection;
};

export type CostCenterListItem = {
  companyId: UUID;
  companyName: string;
  createdOn: string;
  end: string;
  id: UUID;
  isActive: boolean;
  isSynced: boolean;
  modifiedOn: string;
  name: string;
  start: string;
  syncKey: string | null;
  type: string;
};

type CostCentersResponse = {
  data: readonly CostCenterListItem[];
  hasNextPage: boolean;
  paginatedCount: number;
  totalCount: number;
};

const defaultQueryParams = {
  filterAccRefType: undefined,
  filterActive: undefined,
  filterCompany: undefined,
  filterNotParentOu: undefined,
  filterParentOu: undefined,
  limit: PAGINATION_PAGE_SIZE_DEFAULT,
  offset: 0,
  orderBy: 'name',
  returnTotalCount: true,
  searchString: undefined,
  sort: 'ASC',
};

/**
 * Generates the query options for the cost centers query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param {Object} queryParams - The query parameters for fetching cost centers.
 * @param {Object} options - Additional options for the query.
 * @returns {Object} The query options including queryKey, queryFn, and other settings.
 */
export const getCostCentersQueryOptions = ({
  queryParams,
  options,
}: {
  queryParams: Partial<CostCentersQueryParams>;
  options?: Omit<UseQueryOptions<CostCentersResponse>, 'queryKey' | 'queryFn'>;
}) => {
  const qp = {
    ...defaultQueryParams,
    ...queryParams,
  };

  qp.sort = qp.sort?.toUpperCase();

  return {
    queryFn: async () => fetchCostCenters(qp),
    queryKey: queryKeysCostCenter.getAll(qp),
    staleTime: ms('60s'), // prevent hitting the rate limit of 30 requests/s
    ...options,
  };
};

/**
 * Fetches all cost centers from the API.
 * @param {Object} queryParams - The query parameters for fetching cost centers.
 * @returns {Promise<Object|null>} The cost center data if successful, null otherwise.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Accounting-Reference/operation/get_accounting_references_accounting_reference_get
 */
export const fetchCostCenters = async (
  queryParams: Partial<CostCentersQueryParams>,
) => {
  const mergedParams = {
    ...defaultQueryParams,
    ...queryParams,
  };

  const qp = withoutObjectKeysWhereValueIs(
    {
      ...mergedParams,
      orderBy: mergedParams.orderBy
        ? toSnakeCase(mergedParams.orderBy)
        : undefined,
      sort: mergedParams.sort ? mergedParams.sort.toUpperCase() : undefined,
    },
    [undefined],
  );

  const response = await vestigasApi
    .get(ENDPOINT.COST_CENTER.GET_ALL(), {
      searchParams: qp,
    })
    .json<CostCentersResponse>();

  return (
    response ?? {
      data: [],
      hasNextPage: false,
      paginatedCount: 0,
      totalCount: 0,
    }
  );
};

/**
 * React Query based custom hook for getting the data for all cost centers with given query parameters.
 * Handles pagination and prefetches the next page of cost centers for better performance.
 *
 * @param {Object} queryParams - The query parameters for fetching cost centers.
 * @param {Object} options - Additional options for the useQuery hook.
 * @returns {UseQueryResult} The result of the useQuery hook.
 */
export const useQueryCostCenters = (queryParams, options) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (queryParams?.searchString?.length > 0) {
      // Only prefetch if the user is not searching, to prevent firing lots of requests for costly queries.
      return;
    }

    queryClient.prefetchQuery(
      getCostCentersQueryOptions({
        options,
        queryParams: {
          ...queryParams,
          offset: Number(queryParams.offset) + Number(queryParams.limit),
        },
      }),
    );
  }, [queryParams, options, queryClient]);

  return useQuery({
    ...getCostCentersQueryOptions({ options, queryParams }),
    placeholderData: keepPreviousData,
  });
};

/**
 * React Query based custom hook for getting the data for all cost centers with infinite scrolling.
 * Uses useInfiniteQuery to handle loading more data as the user scrolls.
 *
 * @param {Object} queryParams - The query parameters for fetching cost centers.
 * @param {Object} options - Additional options for the useInfiniteQuery hook.
 * @returns {UseInfiniteQueryResult} The result of the useInfiniteQuery hook.
 */
export const useQueryCostCentersInfinite = (
  queryParams: Partial<CostCentersQueryParams>,
  options: InfiniteQueryOptionsType<any, Error>,
) => {
  const qp = {
    ...defaultQueryParams,
    ...queryParams,
  };

  const infiniteQueryOptions = {
    queryFn: async ({ pageParam: pageParameter = 0 }) =>
      fetchCostCenters({
        ...qp,
        offset: pageParameter,
      }),
    queryKey: queryKeysCostCenter.getAllInfinite(queryParams),
    ...options,
  };

  return createInfiniteQuery<any>(qp, infiniteQueryOptions);
};
